import { inject } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  collection,
  Firestore,
  FirestoreDataConverter,
  limit,
  orderBy,
  query,
  where,
} from '@fido/firebase/firestore';
import type { ImportBatch } from 'fido-types';
import { ImportBatchSchema, orgImportBatchesPath } from 'fido-types';
import { createInjectionToken } from 'ngxtension/create-injection-token';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { observeCollection } from '../../firebase/observe-collection';
import { injectCurrentOrganization } from '../../organizations/data/current-organization';

export interface ImportBatchWithId extends ImportBatch {
  readonly id: string;
}

export const converter: FirestoreDataConverter<ImportBatchWithId> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot) => ({
    id: snapshot.id,
    ...ImportBatchSchema.parse(snapshot.data()),
  }),
};

export const [injectImportBatches, provideImportBatches] = createInjectionToken(() => {
  const firestore = inject(Firestore);
  const organization = injectCurrentOrganization();

  return rxResource({
    params: () => organization.value()?.id,
    stream: ({ params: orgId }) =>
      observeCollection(
        query(
          collection(firestore, orgImportBatchesPath(orgId)),
          orderBy('uploadedAt', 'desc'),
          limit(50),
        ).withConverter(converter),
        'Import Batches',
      ),
  });
});

/**
 * Returns a function that watches the importBatches collection for a specific
 * storagePath. Used by ImportWizard to track processing progress after upload.
 */
export const [injectWatchImportBatch, provideWatchImportBatch] = createInjectionToken(() => {
  const firestore = inject(Firestore);
  const organization = injectCurrentOrganization();

  return (storagePath: string): Observable<ImportBatchWithId | undefined> => {
    const org = organization.value();
    if (!org) return of(undefined);

    return observeCollection(
      query(
        collection(firestore, orgImportBatchesPath(org.id)),
        where('storagePath', '==', storagePath),
        limit(1),
      ).withConverter(converter),
      'Import Batch by Storage Path',
    ).pipe(map((batches) => batches[0]));
  };
});
